package com.arena_web.data.dto

import kotlinx.serialization.Serializable

@Serializable
data class ClubDto(
    val id: String,
    val ownerId: String,
    val name: String,
    val address: String,
    /** Grouping label (Spanish province / city) — server-populated; clients
     *  fall back to parsing the address when null. */
    val city: String? = null,
    /** ISO-3166-1 alpha-2 country code (e.g. "ES", "UA") for the flag. */
    val country: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val type: String,
    /** Full sport set (primary included) for multi-sport clubs; empty on old
     *  payloads — fall back to [type]. */
    val types: List<String> = emptyList(),
    val customSportName: String? = null,
    val mainPhotoUrl: String? = null,
    val phone: String? = null,
    val description: String? = null,
    val instagramUrl: String? = null,
    val websiteUrl: String? = null,
    val googleMapsUrl: String? = null,
    val googleSearchUrl: String? = null,
    val averageRating: Double = 0.0,
    val totalRatingsCount: Int = 0,
    val isActive: Boolean = true,
    val gallery: List<ClubGalleryImageDto>? = null,
    val rules: List<ClubRuleDto>? = null,
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class ClubGalleryImageDto(
    val id: String,
    val imageUrl: String,
    val sortOrder: Int
)

@Serializable
data class ClubRuleDto(
    val id: String,
    val text: String,
    val sortOrder: Int
)

@Serializable
data class FieldDto(
    val id: String,
    val clubId: String,
    val name: String,
    val type: String,
    val surface: String,
    val isIndoor: Boolean = false,
    val capacity: Int? = null,
    val imageUrl: String? = null,
    val isActive: Boolean = true,
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class PublicClubDetailDto(
    val club: ClubDto,
    val gallery: List<ClubGalleryImageDto>,
    val rules: List<ClubRuleDto>,
    val fields: List<FieldDto>,
    val services: List<ClubServiceDto> = emptyList(),
    /** AmenityType names (PARKING, WIFI, …) the club actually has. */
    val amenities: List<String> = emptyList()
)

@Serializable
data class ClubReviewDto(
    val id: String,
    val clubId: String,
    val userId: String,
    val userName: String,
    val userAvatarUrl: String? = null,
    val rating: Double,
    val comment: String? = null,
    val createdAt: String
)

@Serializable
data class CreateClubReviewRequest(
    val rating: String,
    val comment: String? = null
)

@Serializable
data class ClubReviewPageDto(
    val items: List<ClubReviewDto>,
    val page: Int,
    val size: Int,
    val totalItems: Long,
    val totalPages: Int
)

@Serializable
data class ClubListPageDto(
    val clubs: List<ClubDto>,
    val total: Long,
    val page: Int,
    val size: Int
)

@Serializable
data class ClubCityDto(
    val city: String,
    val country: String? = null,
    val count: Long = 0
)

@Serializable
data class ClubCityListDto(
    val cities: List<ClubCityDto> = emptyList()
)

@Serializable
data class FieldAvailabilityDto(
    val date: String,
    val slots: List<TimeSlotAvailabilityDto>
)

@Serializable
data class TimeSlotAvailabilityDto(
    val startTime: String,
    val endTime: String,
    val isAvailable: Boolean
)

@Serializable
data class CreateClubRequest(
    val name: String,
    val address: String,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val type: String? = null,
    val customSportName: String? = null,
    val mainPhotoUrl: String? = null,
    val phone: String? = null,
    val description: String? = null
)

@Serializable
data class UpdateClubRequest(
    val name: String? = null,
    val address: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val type: String? = null,
    val customSportName: String? = null,
    val mainPhotoUrl: String? = null,
    val phone: String? = null,
    val description: String? = null,
    /** Blank string clears the link; null leaves it unchanged. */
    val instagramUrl: String? = null,
    val websiteUrl: String? = null,
    val googleSearchUrl: String? = null,
    val googleMapsUrl: String? = null
)

@Serializable
data class UpdateClubGalleryRequest(
    val images: List<GalleryImageEntry>
)

@Serializable
data class GalleryImageEntry(
    val imageUrl: String
)

@Serializable
data class UpdateClubRulesRequest(
    val rules: List<RuleTextEntry>
)

@Serializable
data class RuleTextEntry(
    val text: String
)

@Serializable
data class CreateFieldRequest(
    val name: String,
    val type: String,
    val surface: String,
    val isIndoor: Boolean = false,
    val capacity: Int? = null,
    val imageUrl: String? = null
)

@Serializable
data class UpdateFieldRequest(
    val name: String? = null,
    val type: String? = null,
    val surface: String? = null,
    val isIndoor: Boolean? = null,
    val capacity: Int? = null,
    val imageUrl: String? = null
)
